<template>
  <div class="p-3 bg-white rounded shadow-sm" data-test="contest-result" :data-contest-result="`contest_${contestReference}`" v-if="contestResult.status !== 'pending'">
    <ResultCeremonyActions
      :title="contestTitle"
      resource-type="contest_results"
      :resource-id="contestResult.id"
      :can-retry="contestResult.status === 'mapping' || isAggregating"
      :can-reset="contestResult.status === 'mapping' || isAggregating || contestResult.status === 'finished'"
    />
    <div class="accent-line mt-2 mb-4"></div>

    <div v-if="contestResult.error">
      <details class="alert alert-warning mb-2">
        <summary class="fw-bold">{{ t("js.election_client.error.headers.an_error_occurred") }}</summary>
        <div>
          <p class="mb-0">{{ t("backend.e2e.voting_round_reports.show.error_body") }}</p>
        </div>
      </details>
    </div>

    <div v-if="result && result.resultData">
      <ResultOrchestratorResults
        :voting-round-report="votingRoundReport"
        :contest="contest"
        :result="result"
      />
      <div class="mt-4 small">
        <TurnoutTable
          :turnout="result.voterCounts"
          :hide-weight="contest.disregardWeights"
        />
      </div>

      <div class="mt-4 small">
        {{ t("backend.e2e.voting_round_reports.show.invalid_votes") }}: {{ result.voterCounts.invalidVotes }} <br />
        {{ t("backend.e2e.voting_round_reports.show.disabled_votes") }}: {{ result.voterCounts.disabledWeight }}
      </div>

      <ResultOrchestratorExternalVotes
        v-if="externalVotes"
        :external-votes="externalVotes"
      />
    </div>
    <div v-else-if="contestResult.status === 'mapping'">
      <WriteInMap :contest-result="contestResult" :contest="contest" />
    </div>
    <div v-else-if="isAggregating">
      <div class="hstack align-items-center gap-2 mb-2">
        <b>{{ t("js.result_progress.status") }}:</b>
        <span class="me-2">{{ t(`js.result_progress.states.${contestResult.status}`) }}</span>
      </div>

      <AVProgressBar
        :id="`contest_result_${contestResult.id}`"
        :value="progressPercentage"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import ResultCeremonyActions from "@/components/backend/result_orchestrator/ResultCeremonyActions.vue";
import ResultOrchestratorResults from "@/components/backend/result_orchestrator/ResultOrchestratorResults.vue";
import ResultOrchestratorExternalVotes from "@/components/backend/result_orchestrator/ResultOrchestratorExternalVotes.vue";
import TurnoutTable from "@/components/backend/live/TurnoutTable.vue";
import WriteInMap from "@/components/backend/WriteInMap.vue";
import { flattenOptions } from "@/entrypoints/shared/contest_utilities";
import { useI18n } from "vue-i18n";
import type {
  ConferenceContest,
  ConferenceOption,
  PropType,
  E2E,
  Result,
  SupportedLocale,
} from "@/types";

const { locale, t } = useI18n();

const props = defineProps({
  contestResult: {
    type: Object as PropType<E2E.ContestResult>,
    required: true,
  },
  votingRoundReport: {
    type: Object as PropType<E2E.VotingRoundReport>,
    required: true,
  },
  contest: {
    type: Object as PropType<ConferenceContest>,
    required: true,
  },
  resultOrchestrators: {
    type: Array as PropType<Array<E2E.ResultOrchestrator>>,
    default: () => [],
  },
});

const contestReference = computed(() => props.contestResult.contestReference);
const contestTitle = computed(() => props.contest.title?.[locale.value as SupportedLocale] ?? "");
const result = computed<Result>(() => props.contestResult.result as Result);

const optionsFlattened = computed(() => flattenOptions(props.contest.options));

const externalVotes = computed((): undefined | Record<string, number | Record<string, number>> => {
  let externalVotes = {}
  const resultData = (result.value as Result).resultData.externalVotes?.[props.contest.reference];
  if (!resultData) return;

  Object.values(optionsFlattened.value).forEach((option: ConferenceOption) => {
    if (!option.reference || option.reference == "blank") return;
    externalVotes[option.title[locale.value] || Object.values(option.title)[0]] = resultData[option.reference];
  });

  if (props.contest.blankOption !== "disabled") externalVotes[t("js.standard_option.blank")] = resultData["blank"];

  return externalVotes;
});


const isAggregating = computed(() => props.contestResult.status === "aggregating");

const progressPercentage = computed(() => (props.contestResult.progress ?? 0) * 100);
</script>
